import React, { createContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { responsiveFontSizes } from '@mui/material/styles';

import { paletteGreen } from './palettes/green';
import { paletteBlue } from './palettes/blue';
import { paletteRed } from './palettes/red';
import { palettePurple } from './palettes/purple';
import { paletteOrange } from './palettes/orange';
import { typography } from './typography';
import { getComponentOverrides } from './components';

// Map of per-theme primary colors — consumed by the theme switcher UI
export const THEME_COLORS = {
  green: paletteGreen.primary.main,
  blue: paletteBlue.primary.main,
  red: paletteRed.primary.main,
  purple: palettePurple.primary.main,
  orange: paletteOrange.primary.main,
};

export const ColorModeContext = createContext({
  toggleTheme: () => { },
  toggleDarkMode: () => { },
  currentTheme: 'green',
  isDarkMode: false,
  themeColors: THEME_COLORS,
});

export function AppThemeProvider({ children }) {
  // Inicializamos el tema verde por defecto (o leyendo localStorage si quisiéramos persistirlo)
  // OPCIÓN DE COLOR: Puedes cambiar 'green' por 'blue' o 'red' o purple o black o orange directamente aquí
  const [themeName, setThemeName] = useState('blue');
  const [darkMode, setDarkMode] = useState(false);

  const themeMode = useMemo(() => {
    let activePalette;

    switch (themeName) {
      case 'blue':
        activePalette = paletteBlue;
        break;
      case 'red':
        activePalette = paletteRed;
        break;
      case 'purple':
        activePalette = palettePurple;
        break;
      case 'orange':
        activePalette = paletteOrange;
        break;
      case 'green':
      default:
        activePalette = paletteGreen;
        break;
    }

    // Background/text differ by mode; everything else comes from the active palette.
    const modePalette = darkMode
      ? {
          background: {
            default: '#121212',
            paper: '#1E1E1E',
            neutral: '#282C34',
          },
          text: {
            primary: '#ffffff',
            secondary: 'rgba(255, 255, 255, 0.7)',
            disabled: 'rgba(255, 255, 255, 0.5)',
          },
        }
      : {
          background: activePalette.background,
          text: activePalette.text,
        };

    // Palette handed to typography & component overrides so they follow the
    // active mode. Without this, dark mode inherited light backgrounds/text
    // (e.g. the DataGrid stayed white on /users/list).
    const overridePalette = {
      ...activePalette,
      ...modePalette,
      brand: darkMode
        ? { ...activePalette.brand, rowHover: 'rgba(255, 255, 255, 0.08)' }
        : activePalette.brand,
    };

    let theme = createTheme({
      palette: {
        mode: darkMode ? 'dark' : 'light',
        primary: activePalette.primary,
        secondary: activePalette.secondary,
        error: activePalette.error,
        warning: activePalette.warning,
        success: activePalette.success,
        info: activePalette.info,
        divider: activePalette.divider,
        brand: overridePalette.brand,
        // DataGrid v9 lee esta clave para el fondo de los encabezados (design
        // tokens); sin ella el encabezado usa el color del papel y el texto
        // blanco del override queda invisible en modo claro.
        DataGrid: {
          headerBg: activePalette.primary.main,
        },
        ...modePalette,
      },
      typography: typography(overridePalette),
      components: getComponentOverrides(overridePalette),
      shape: {
        borderRadius: 12,
      },
    });

    theme = responsiveFontSizes(theme);
    return theme;

  }, [themeName, darkMode]);

  const toggleTheme = (newThemeName) => {
    setThemeName(newThemeName);
  };

  const toggleDarkMode = () => {
    setDarkMode((prev) => !prev);
  };

  return (
    <ColorModeContext.Provider value={{
      toggleTheme,
      toggleDarkMode,
      currentTheme: themeName,
      isDarkMode: darkMode,
      themeColors: THEME_COLORS
    }}>
      <MUIThemeProvider theme={themeMode}>
        {children}
      </MUIThemeProvider>
    </ColorModeContext.Provider>
  );
}
